import { forwardRef, useId } from "react";
import { cx } from "@/utils/cx";
import type { InputHTMLAttributes, ReactNode } from "react";

/**
 * Gymkee 2027 checkbox (spec checkbox 1.7; sheet DS05).
 *
 * The box, the corner, the tick and the ring are drawn in `.ds-checkbox`; the browser's own
 * control never shows. The box aligns to the first line of a label that wraps rather than
 * centring against it, and the whole label is part of the target, because on this page the
 * label is a two-sentence consent and a 20 box on its own is not a hit area.
 */

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  /** The sentence the person is agreeing to; it may carry a link. */
  label: ReactNode;
  className?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, className, id: givenId, ...props }, ref) => {
    const generated = useId();
    const id = givenId ?? `checkbox-${generated}`;

    return (
      /* The whole row is the target and it is never under the touch minimum: 48 is the hand step
       of the control scale, and the box stays aligned to the FIRST line of a label that wraps
       rather than being centred against it (checkbox 1.7 doNot 4). */
      <div className={cx("gap-gk-3 min-h-gk-12 flex items-start", className)}>
        <input
          ref={ref}
          id={id}
          type="checkbox"
          className="ds-checkbox mt-gk-1"
          {...props}
        />
        <label
          htmlFor={id}
          className="min-w-0 cursor-pointer text-[14px] leading-[1.5] [overflow-wrap:anywhere] text-[var(--gk-text-primary)]"
        >
          {label}
        </label>
      </div>
    );
  },
);

Checkbox.displayName = "Checkbox";
